/**
 * Idiomas públicos del site. Añadir uno aquí es suficiente para i18n, SEO,
 * el proxy de contenido y el resto de comprobaciones de locale.
 */
export const LOCALES = [
  { code: 'es', country: 'ES', name: 'Español', language: 'es', gtm: 'spa' },
  { code: 'en', country: 'GB', name: 'English', language: 'en', gtm: 'eng' },
  { code: 'de', country: 'DE', name: 'Deutsch', language: 'de', gtm: 'deu' },
  { code: 'it', country: 'IT', name: 'Italiano', language: 'it', gtm: 'ita' },
  { code: 'fr', country: 'FR', name: 'Français', language: 'fr', gtm: 'fra' },
] as const

export type AppLocale = (typeof LOCALES)[number]

export type LocaleCode = AppLocale['code']

export const LOCALE_CODES: readonly LocaleCode[] = LOCALES.map((locale) => locale.code)

export const DEFAULT_LOCALE: LocaleCode = 'es'

export function isLocaleCode(value: string): value is LocaleCode {
  return LOCALE_CODES.some((code) => code === value)
}

/** Locale pedido por query; si no está soportado, el idioma por defecto. */
export function localeOrDefault(value: string | undefined): LocaleCode {
  return value !== undefined && isLocaleCode(value) ? value : DEFAULT_LOCALE
}

export function localeFromPath(path: string): LocaleCode | null {
  const segment = path.split('/').filter(Boolean)[0]
  if (!segment || !isLocaleCode(segment)) return null
  return segment
}

export function localeFromUrl(url: string): LocaleCode | null {
  try {
    return localeFromPath(new URL(url).pathname)
  } catch {
    return localeFromPath(url)
  }
}

export function localeFromAcceptLanguage(header: string | undefined): LocaleCode {
  if (!header) return DEFAULT_LOCALE
  const preferred = header.split(',')[0]?.split('-')[0]?.toLowerCase()
  if (preferred && isLocaleCode(preferred)) return preferred
  return DEFAULT_LOCALE
}

/** Locale para APIs servidor: prefijo en Referer, si no Accept-Language, si no `es`. */
export function resolveLocale(
  referer?: string | null,
  acceptLanguage?: string | null
): LocaleCode {
  if (referer) {
    const fromReferer = localeFromUrl(referer)
    if (fromReferer) return fromReferer
  }
  return localeFromAcceptLanguage(acceptLanguage ?? undefined)
}
